import { getConfig } from "@stryker/core/api/jobs";
import { QueryKeys } from "@stryker/core/queryKeys";
import type { CreateEditJobOptions } from "@stryker/core/types/jobs"; // Import the actual API response type
import { queryOptions, useQuery } from "@tanstack/react-query";

// Define types to match CreateEditJobOptions exactly (uncommented and exported).
// These align with inline types in @stryker/core/types/jobs to avoid duplication/mismatches.
export interface SurfacePreparation {
  id: number;
  name: string;
  long_name: string;
  repair_requirements_note: string;
}

export type Kits = {
  id: number;
  name: null | string;
  length: number;
  width: number;
  unit?: string;
  cost_per_unit?: number;
  price_per_unit?: number;
};

export interface RepairSystem {
  id: number;
  kits: Kits[]; // Align with CreateEditJobOptions (uses Kits[])
  name: string;
  repair_system_max_temp: number;
  thickness_of_wrap_layer: string;
  toughness_parameter: string;
  poisson_ratio: string;
  thermal_expansion_coefficient: string;
  lap_shear_strength: number;
  glass_transition_temperature: string;
  tensile_modulus_axial: number;
  tensile_modulus_circumferential: number;
  thermal_expansion_in_axial_direction_of_the_composite: string;
  shear_modulus: number;
  test_layer: number;
  repair_requirements_note: string | null;
  organization: number;
}

export interface RepairOption {
  id: number;
  name: string;
  type: string;
  unit: string;
  cost_per_unit: number;
  price_per_unit: number;
}

export interface DiameterToThickness {
  nps: string;
  schedule: string[];
}

export interface JobType {
  id: number;
  name: string;
}

export interface MaterialOption {
  id: number;
  material: string;
  type: string;
}

export interface MaterialGroup {
  group: string;
  options: MaterialOption[];
}

export type MechanicalCalculationCases = string[];
export type DesignCodes = string[];
export type Shells = MaterialGroup[];
export type Endplates = MaterialGroup[];
export type Ears = MaterialGroup[];
export type FlangeClamps = MaterialGroup[];
export type StudBolts = MaterialGroup[];

// New types for seal-related fields
export type SealPreferences = string[];
export type SealTypes = string[];
export type SealMaterials = string[];

export const createEditJobOptionsQuery = queryOptions<CreateEditJobOptions>({
  queryKey: [QueryKeys.JobsConfig],
  queryFn: getConfig,
  staleTime: 1000000,
});

export function useGetConfig() {
  return useQuery<CreateEditJobOptions>({
    ...createEditJobOptionsQuery,
  });
}

export function useSurfacePreparations() {
  return useQuery({
    ...createEditJobOptionsQuery,
    select: (data: CreateEditJobOptions | undefined) => data?.surface_preparations ?? [],
  });
}

export function useRepairSystems() {
  return useQuery({
    ...createEditJobOptionsQuery,
    select: (data: CreateEditJobOptions | undefined) => data?.repair_systems ?? [],
  });
}

export function useRepairOptions() {
  return useQuery({
    ...createEditJobOptionsQuery,
    select: (data: CreateEditJobOptions | undefined) => data?.repair_options ?? [],
  });
}

export function useDiameterToThickness() {
  return useQuery({
    ...createEditJobOptionsQuery,
    select: (data: CreateEditJobOptions | undefined) => data?.diameter_to_thickness ?? [],
  });
}

export function useJobTypes() {
  return useQuery({
    ...createEditJobOptionsQuery,
    select: (data: CreateEditJobOptions | undefined) => data?.job_types ?? [],
  });
}

export function useMechanicalCalculationCases() {
  return useQuery({
    ...createEditJobOptionsQuery,
    select: (data: CreateEditJobOptions | undefined) => data?.mechanical_calculation_cases ?? [],
  });
}

export function useDesignCodes() {
  return useQuery({
    ...createEditJobOptionsQuery,
    select: (data: CreateEditJobOptions | undefined) => data?.design_codes ?? [],
  });
}

export function useShells() {
  return useQuery({
    ...createEditJobOptionsQuery,
    select: (data: CreateEditJobOptions | undefined) => data?.shells ?? [],
  });
}

export function useEndplates() {
  return useQuery({
    ...createEditJobOptionsQuery,
    select: (data: CreateEditJobOptions | undefined) => data?.endplates ?? [],
  });
}

export function useEars() {
  return useQuery({
    ...createEditJobOptionsQuery,
    select: (data: CreateEditJobOptions | undefined) => data?.ears ?? [],
  });
}

export function useFlangeClamps() {
  return useQuery({
    ...createEditJobOptionsQuery,
    select: (data: CreateEditJobOptions | undefined) => data?.flange_clamps ?? [],
  });
}

export function useStudBolts() {
  return useQuery({
    ...createEditJobOptionsQuery,
    select: (data: CreateEditJobOptions | undefined) => data?.stud_bolts ?? [],
  });
}

// New hooks for seal-related fields
export function useSealPreferences() {
  return useQuery({
    ...createEditJobOptionsQuery,
    select: (data: CreateEditJobOptions | undefined) => data?.seal_preferences ?? [],
  });
}

export function useSealTypes() {
  return useQuery({
    ...createEditJobOptionsQuery,
    select: (data: CreateEditJobOptions | undefined) => data?.seal_types ?? [],
  });
}

export function useSealMaterials() {
  return useQuery({
    ...createEditJobOptionsQuery,
    select: (data: CreateEditJobOptions | undefined) => data?.seal_materials ?? [],
  });
}

export function useSealSizes() {
  return useQuery({
    ...createEditJobOptionsQuery,
    select: (data: CreateEditJobOptions | undefined) => data?.seal_sizes ?? [],
  });
}

export function useStrongbacks() {
  return useQuery({
    ...createEditJobOptionsQuery,
    select: (data: CreateEditJobOptions | undefined) => data?.strongbacks ?? [],
  });
}

export function useStrongbacksStudBolts() {
  return useQuery({
    ...createEditJobOptionsQuery,
    select: (data: CreateEditJobOptions | undefined) => data?.strongbacks_stud_bolts ?? [],
  });
}
